/**
 * @file useLockedPageScroll.ts
 * @description Запрет прокрутки всей страницы, пока открыт конструктор сайта
 * и его рабочая область закреплена от шапки до низа окна (с 1024,
 * `workspaceAreaClass`; замечание владельца 2026-10-18): у него документ
 * оказывался выше окна, страница уезжала под шапку, снизу оставалось серое
 * поле. Прокручиваются только три области — структура, канва и панель
 * настроек; у них `overscroll-contain`, чтобы колесо на краю области не
 * уходило наружу. На 768–1023 (верхняя и нижняя полосы в потоке) и на
 * телефоне страница прокручивается как обычно.
 *
 * Строки стиля `<html>` и `<body>` не трогаем: ставим атрибут
 * `html[data-page-scroll-locked]`, правило в index.css даёт `overflow:
 * hidden`. Так блокировка окон Base UI и
 * наша друг друга не затирают: Base UI видит `hidden` у `<html>` и ничего
 * не делает, а если Base UI заблокировал первым (шторка меню, окно), он
 * вернёт свои значения сам, а мы при уходе только снимем атрибут (ревью Б1).
 */

import { useEffect } from "react";

/** Атрибут на `<html>`, по которому index.css запрещает прокрутку страницы. */
export const PAGE_SCROLL_LOCK_ATTR = "data-page-scroll-locked";

/** Ставит запрет прокрутки документа и возвращает функцию снятия. */
export function lockPageScroll(doc: Document = document): () => void {
  const html = doc.documentElement;
  html.setAttribute(PAGE_SCROLL_LOCK_ATTR, "");
  return () => html.removeAttribute(PAGE_SCROLL_LOCK_ATTR);
}

/** Пока `on` — прокрутка всей страницы запрещена. */
export function useLockedPageScroll(on = true): void {
  useEffect(() => {
    if (!on) return;
    return lockPageScroll();
  }, [on]);
}
